import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ReportLeadGate — the single modal shown by every calculator/tool BEFORE
 * the report is revealed. Enforces name + email + phone capture so ad
 * traffic converts into named leads instead of anonymous browsers.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void   (user dismissed without submitting)
 *  - onSubmit: async ({name, email, phone}) => void
 *      Parent runs the actual calc + reveals the report. If the promise
 *      rejects, the modal stays open so the user can retry.
 *  - toolLabel: string     (e.g. "Feasibility Report") — used in copy only
 *  - initial: {name, email, phone}   optional pre-fill from parent form
 *
 * The gate itself does NOT hit the backend — the parent's calculate endpoint
 * receives name/email/phone in its normal payload and creates the lead there.
 * That keeps the write path unified and the audit trail intact.
 */
export default function ReportLeadGate({ open, onClose, onSubmit, toolLabel = 'Report', initial = {} }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    email: initial.email || '',
    phone: initial.phone || '',
  });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!form.email.trim()) return 'Please enter your email.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'That email doesn\u2019t look right.';
    if (!form.phone.trim()) return 'Please enter your phone number.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
      });
    } catch (err) {
      // Parent already toasted the failure. Keep the modal open so the user
      // can retry without re-typing.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="report-lead-gate"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-sm border border-black/10 overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/10 bg-brand-dark text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center">
              <Lock className="w-4 h-4 text-brand-accent" />
            </div>
            <div>
              <p className="text-sm font-body font-medium leading-tight">Your {toolLabel} is ready</p>
              <p className="text-[11px] font-body opacity-70 leading-tight">Share your details to reveal it</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="report-lead-gate-close"
            className="text-white/60 hover:text-white p-1 disabled:opacity-30"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3.5" data-testid="report-lead-gate-form">
          <p className="text-xs font-body text-brand-dark/60 leading-relaxed">
            We&apos;ll unlock your full report and send a PDF copy to your inbox. No spam — used only to follow up on this enquiry.
          </p>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Name</label>
            <Input
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Your full name"
              className="rounded-sm h-10 font-body text-sm"
              data-testid="report-lead-gate-name"
              autoFocus
              disabled={submitting}
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Email</label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update({ email: e.target.value })}
              placeholder="you@example.com"
              className="rounded-sm h-10 font-body text-sm"
              data-testid="report-lead-gate-email"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Phone</label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => update({ phone: e.target.value })}
              placeholder="+27 ..."
              className="rounded-sm h-10 font-body text-sm"
              data-testid="report-lead-gate-phone"
              disabled={submitting}
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="report-lead-gate-submit"
            className="w-full bg-brand-dark hover:bg-black text-white rounded-sm h-11 font-body text-xs uppercase tracking-wider mt-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Reveal my ${toolLabel.toLowerCase()}`}
          </Button>
          <p className="text-[10px] font-body text-brand-dark/40 text-center">
            By continuing you agree Raum may contact you about your enquiry.
          </p>
        </form>
      </div>
    </div>
  );
}
